import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Heart, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../App';

// Returns seasonal context based on current date
const getSeason = () => {
  const now = new Date();
  const m = now.getMonth();
  const d = now.getDate();
  // Christmas window: Nov 20 - Dec 31
  if ((m === 10 && d >= 20) || m === 11) return 'christmas';
  // Valentine's: Jan 25 - Feb 14
  if ((m === 0 && d >= 25) || (m === 1 && d <= 14)) return 'valentines';
  // Mother's Day window: Apr 25 - May 14 (CH = 2nd Sunday of May)
  if ((m === 3 && d >= 25) || (m === 4 && d <= 14)) return 'mothers';
  // Father's Day window: May 25 - Jun 14
  if ((m === 4 && d >= 25) || (m === 5 && d <= 14)) return 'fathers';
  return 'default';
};

const SEASONAL_COPY = {
  christmas: {
    it: { eyebrow: '✨ Edizione di Natale', title: 'Sotto l\'albero, regala benessere.' },
    de: { eyebrow: '✨ Weihnachtsedition', title: 'Schenken Sie Wohlbefinden unter dem Baum.' },
  },
  valentines: {
    it: { eyebrow: '♥ San Valentino', title: 'Un regalo che parla al cuore.' },
    de: { eyebrow: '♥ Valentinstag', title: 'Ein Geschenk, das zu Herzen geht.' },
  },
  mothers: {
    it: { eyebrow: '🌷 Festa della Mamma', title: 'Per la mamma, un momento solo per lei.' },
    de: { eyebrow: '🌷 Muttertag', title: 'Für Mama: ein Moment nur für sie.' },
  },
  fathers: {
    it: { eyebrow: '👔 Festa del Papà', title: 'Un attimo di quiete per il papà.' },
    de: { eyebrow: '👔 Vatertag', title: 'Ein Moment der Ruhe für den Papa.' },
  },
  default: {
    it: { eyebrow: 'Buoni Regalo', title: 'Regala un momento di benessere.' },
    de: { eyebrow: 'Geschenkgutscheine', title: 'Schenken Sie einen Moment des Wohlbefindens.' },
  },
};

const VoucherBanner = () => {
  const { language, localePath } = useLanguage();
  const season = getSeason();
  const copy = SEASONAL_COPY[season][language] || SEASONAL_COPY.default[language];

  const t = language === 'it' ? {
    subtitle: 'Massaggio medicale CRS, riconosciuto dalle casse malati. Da 1 a 10 sedute, validi 12 mesi.',
    cta: 'Acquista Buono Regalo',
    pkg1: '1 massaggio',
    pkg3: '3 massaggi',
    pkg5: '5 massaggi',
    pkg10: '10 massaggi',
    from: 'da',
  } : {
    subtitle: 'Anerkannte medizinische Massage. Von 1 bis 10 Sitzungen, 12 Monate gültig.',
    cta: 'Gutschein kaufen',
    pkg1: '1 Massage',
    pkg3: '3 Massagen',
    pkg5: '5 Massagen',
    pkg10: '10 Massagen',
    from: 'ab',
  };

  return (
    <section data-testid="voucher-banner" className="relative overflow-hidden py-20 bg-gradient-to-br from-pink-900 via-pink-800 to-rose-900">
      {/* Decorative blurred orbs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-rose-300/20 rounded-full blur-3xl" aria-hidden="true" />
      {/* Floating sparkles */}
      <div className="absolute inset-0 opacity-30" aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <Sparkles
            key={i}
            className="absolute text-pink-200 animate-pulse"
            size={12 + (i % 3) * 6}
            style={{
              left: `${10 + i * 14}%`,
              top: `${15 + (i * 11) % 70}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: '3s',
            }}
          />
        ))}
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3 text-center lg:text-left"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-pink-50/15 backdrop-blur-sm border border-pink-200/30 rounded-full text-pink-100 text-xs font-medium tracking-wider uppercase mb-5" data-testid="banner-eyebrow">
              {copy.eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4 tracking-tight leading-tight" data-testid="banner-title">
              {copy.title}
            </h2>
            <p className="text-lg text-pink-100/90 mb-8 max-w-xl lg:max-w-none mx-auto lg:mx-0 font-light">
              {t.subtitle}
            </p>

            {/* Package chips */}
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-8">
              {[
                { label: t.pkg1, price: 120 },
                { label: t.pkg3, price: 330 },
                { label: t.pkg5, price: 525 },
                { label: t.pkg10, price: 1000 },
              ].map((p, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full text-pink-50 text-sm">
                  <span className="font-medium">{p.label}</span>
                  <span className="text-pink-200/80 text-xs">CHF {p.price}</span>
                </span>
              ))}
            </div>

            <Link
              to={localePath("/buoni-regalo")}
              data-testid="banner-cta"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-pink-900 rounded-full font-bold text-sm uppercase tracking-widest shadow-2xl hover:shadow-pink-300/40 hover:scale-105 transition-all duration-300"
            >
              <Gift className="w-5 h-5" />
              {t.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Visual card column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            whileInView={{ opacity: 1, scale: 1, rotate: -3 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 flex justify-center"
          >
            <div className="relative">
              {/* Card glow */}
              <div className="absolute inset-0 bg-pink-300/30 blur-2xl rounded-3xl" aria-hidden="true" />
              {/* Voucher card mockup */}
              <div className="relative bg-gradient-to-br from-white via-pink-50 to-rose-100 rounded-2xl p-8 shadow-2xl w-72 sm:w-80 border-2 border-pink-200/50">
                {/* Corner ribbon */}
                <div className="absolute -top-2 -right-2 bg-pink-700 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg">
                  Original
                </div>
                <div className="text-center">
                  <Heart className="w-7 h-7 text-pink-600 mx-auto mb-2 fill-pink-200" />
                  <p className="font-serif text-2xl text-pink-900 font-bold">EMI Harmony</p>
                  <p className="text-xs text-pink-700 uppercase tracking-widest mt-1 mb-5">
                    {language === 'it' ? 'Buono Regalo' : 'Geschenkgutschein'}
                  </p>
                  <div className="border-t border-b border-pink-300/40 py-4 mb-4">
                    <p className="text-4xl font-serif text-pink-800 font-bold">CHF 330.-</p>
                    <p className="text-xs text-pink-600/80 mt-1">3 {language === 'it' ? 'massaggi · 60 min' : 'Massagen · 60 Min'}</p>
                  </div>
                  {/* Mini QR-like pattern */}
                  <div className="grid grid-cols-5 gap-0.5 w-16 mx-auto mb-3">
                    {[...Array(25)].map((_, i) => (
                      <div key={i} className={`aspect-square rounded-sm ${[0, 2, 4, 6, 7, 10, 12, 14, 17, 19, 20, 22, 24].includes(i % 25) ? 'bg-pink-900' : 'bg-transparent'}`} />
                    ))}
                  </div>
                  <p className="text-[9px] text-pink-700/60 font-mono">N° EMI-2026-XXXX</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VoucherBanner;
